import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Banknote, AlertCircle, Tag, X, CheckCircle, Building2, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, subtotal, clearCart, itemCount } = useCart();
  const { customer, isAuthenticated, getProfile, token: customerToken } = useCustomerAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [selectedZone, setSelectedZone] = useState(0);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    special_instructions: ''
  });

  useEffect(() => {
    document.title = 'Checkout - SizzleBox';
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();

    // Auto-fill from customer profile if logged in
    if (isAuthenticated) {
      getProfile().then(profileData => {
        if (profileData) {
          setFormData(prev => ({
            ...prev,
            customer_name: profileData.user?.name || prev.customer_name,
            customer_email: profileData.user?.email || prev.customer_email,
            customer_phone: profileData.customer?.phone || prev.customer_phone,
            customer_address: profileData.customer?.address || prev.customer_address,
          }));
        }
      });
      // Fetch loyalty points
      if (customerToken) {
        fetch(`${API}/customer/loyalty`, { headers: { Authorization: `Bearer ${customerToken}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setLoyaltyPoints(data.points || 0); })
          .catch(() => {});
      }
    }

    // Check if cancelled from Stripe
    if (searchParams.get('cancelled')) {
      toast.error('Payment was cancelled');
    }
  }, [searchParams, isAuthenticated, getProfile]);

  const deliveryZones = settings?.delivery_zones || [{ zone: 'Standard', fee: settings?.delivery_fee || 500 }];
  const deliveryFee = deliveryZones[selectedZone]?.fee || settings?.delivery_fee || 500;
  const promoDiscount = promoApplied?.discount_amount || 0;
  const pointsDiscount = usePoints ? pointsToUse * 10 : 0;
  const discount = promoDiscount + pointsDiscount;
  const total = Math.max(0, subtotal + deliveryFee - discount);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, { code: promoInput, subtotal });
      setPromoApplied(res.data);
      toast.success(`Promo code applied! You save ₦${res.data.discount_amount.toLocaleString()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoInput('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Create order
      const orderData = {
        ...formData,
        items: items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image_url: item.image_url
        })),
        payment_method: paymentMethod,
        promo_code: promoApplied?.code || null,
        discount: discount,
        points_used: usePoints ? pointsToUse : 0,
        points_discount: pointsDiscount,
      };

      const orderRes = await axios.post(`${API}/orders`, orderData);
      const order = orderRes.data;

      // Redeem loyalty points if used
      if (usePoints && pointsToUse > 0 && customerToken) {
        try {
          await fetch(`${API}/customer/loyalty/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${customerToken}` },
            body: JSON.stringify({ points: pointsToUse })
          });
        } catch (_) {}
      }

      if (paymentMethod === 'card') {
        // Create Stripe checkout session
        const hostUrl = window.location.origin;
        const checkoutRes = await axios.post(`${API}/payments/checkout?order_id=${order.id}`, {
          host_url: hostUrl
        });
        
        // Redirect to Stripe
        window.location.href = checkoutRes.data.url;
      } else if (paymentMethod === 'transfer') {
        // Redirect to transfer page with order details
        clearCart();
        navigate(`/transfer/${order.id}`);
      } else {
        // Cash payment
        clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order/${order.id}`);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-black pt-28">
        <div className="container mx-auto px-6 text-center">
          <AlertCircle className="w-20 h-20 text-gray-600 mx-auto mb-6" />
          <h1 className="text-3xl text-white mb-4">Your cart is empty</h1>
          <Link to="/menu">
            <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8">
              Browse Menu
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="checkout-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <Logo className="h-12" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6">
          <Link to="/cart" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Cart
          </Link>

          <h1 className="font-heading text-5xl text-white mb-8">Checkout</h1>

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Customer Details */}
              <div className="lg:col-span-2 space-y-8">
                {/* Contact Information */}
                <div className="bg-card rounded-2xl p-6 border border-white/10">
                  <h2 className="text-white font-bold text-xl mb-6">Contact Information</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name" className="text-gray-400 mb-2 block">Full Name *</Label>
                      <Input
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        required
                        className="bg-black/50 border-white/10 text-white"
                        placeholder="John Doe"
                        data-testid="checkout-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_phone" className="text-gray-400 mb-2 block">Phone Number *</Label>
                      <Input
                        id="customer_phone"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleChange}
                        required
                        className="bg-black/50 border-white/10 text-white"
                        placeholder="0801 234 5678"
                        data-testid="checkout-phone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="customer_email" className="text-gray-400 mb-2 block">Email (Optional)</Label>
                      <Input
                        id="customer_email"
                        name="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        className="bg-black/50 border-white/10 text-white"
                        placeholder="john@example.com"
                        data-testid="checkout-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="bg-card rounded-2xl p-6 border border-white/10">
                  <h2 className="text-white font-bold text-xl mb-6">Delivery Address</h2>
                  
                  <div>
                    <Label htmlFor="customer_address" className="text-gray-400 mb-2 block">Full Address</Label>
                    <Input
                      id="customer_address"
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      className="bg-black/50 border-white/10 text-white"
                      placeholder="123 Main Street, Lekki, Lagos"
                      data-testid="checkout-address"
                    />
                  </div>
                  
                  <div className="mt-4">
                    <Label className="text-gray-400 mb-2 block">Delivery Zone</Label>
                    <div className="space-y-2">
                      {deliveryZones.map((zone, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedZone(idx)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                            selectedZone === idx
                              ? 'border-fire-500 bg-fire-500/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                          data-testid={`delivery-zone-${idx}`}
                        >
                          <span className={selectedZone === idx ? 'text-white font-semibold' : 'text-gray-400'}>{zone.zone}</span>
                          <span className={selectedZone === idx ? 'text-fire-500 font-bold' : 'text-gray-400'}>₦{zone.fee?.toLocaleString()}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="special_instructions" className="text-gray-400 mb-2 block">Special Instructions</Label>
                    <Input
                      id="special_instructions"
                      name="special_instructions"
                      value={formData.special_instructions}
                      onChange={handleChange}
                      className="bg-black/50 border-white/10 text-white"
                      placeholder="E.g., Please add extra sauce, no onions..."
                      data-testid="checkout-instructions"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-card rounded-2xl p-6 border border-white/10">
                  <h2 className="text-white font-bold text-xl mb-6">Payment Method</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                        paymentMethod === 'card'
                          ? 'border-fire-500 bg-fire-500/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid="payment-card"
                    >
                      <CreditCard className={`w-8 h-8 ${paymentMethod === 'card' ? 'text-fire-500' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="text-white font-bold">Pay with Card</p>
                        <p className="text-gray-500 text-sm">Secure online payment</p>
                      </div>
                    </button>
                    
                    {/* Cash on Delivery - commented out per business request
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('cash')}
                      className={`p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                        paymentMethod === 'cash'
                          ? 'border-fire-500 bg-fire-500/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid="payment-cash"
                    >
                      <Banknote className={`w-8 h-8 ${paymentMethod === 'cash' ? 'text-fire-500' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="text-white font-bold">Cash on Delivery</p>
                        <p className="text-gray-500 text-sm">Pay when you receive</p>
                      </div>
                    </button>
                    */}
                    
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('transfer')}
                      className={`p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                        paymentMethod === 'transfer'
                          ? 'border-fire-500 bg-fire-500/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid="payment-transfer"
                    >
                      <Building2 className={`w-8 h-8 ${paymentMethod === 'transfer' ? 'text-fire-500' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="text-white font-bold">Bank Transfer</p>
                        <p className="text-gray-500 text-sm">Transfer to our account</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-2xl p-6 border border-white/10 sticky top-28">
                  <h2 className="text-white font-bold text-xl mb-6">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
                    {items.map(item => (
                      <div key={item.menu_item_id} className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm">{item.quantity}x</span>
                          <span className="text-white text-sm">{item.name}</span>
                        </div>
                        <span className="text-white text-sm">₦{(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  {/* Promo Code */}
                  <div className="border-t border-white/10 pt-4 mb-4">
                    <Label className="text-gray-400 mb-2 block text-sm">Promo Code</Label>
                    {promoApplied ? (
                      <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg p-3" data-testid="promo-applied">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-green-400 font-mono font-bold text-sm">{promoApplied.code}</p>
                          <p className="text-green-400/70 text-xs truncate">{promoApplied.description}</p>
                        </div>
                        <button type="button" onClick={handleRemovePromo} className="text-gray-400 hover:text-white" data-testid="remove-promo-btn">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={promoInput}
                          onChange={e => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="bg-black/50 border-white/10 text-white uppercase flex-1"
                          data-testid="promo-input"
                        />
                        <Button
                          type="button"
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoInput.trim()}
                          className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-4"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? '...' : <Tag className="w-4 h-4" />}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Loyalty Points */}
                  {isAuthenticated && loyaltyPoints > 0 && (
                    <div className="border-t border-white/10 pt-4 mb-4" data-testid="loyalty-checkout">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-400 text-sm flex items-center gap-2">
                          <Gift className="w-4 h-4 text-fire-500" />
                          Use Loyalty Points
                        </Label>
                        <button
                          type="button"
                          onClick={() => {
                            setUsePoints(!usePoints);
                            if (!usePoints) {
                              const maxPoints = Math.min(loyaltyPoints, Math.floor((subtotal + deliveryFee - promoDiscount) / 10));
                              setPointsToUse(maxPoints);
                            }
                          }}
                          className={`relative w-11 h-6 rounded-full transition-colors ${usePoints ? 'bg-fire-500' : 'bg-gray-600'}`}
                          data-testid="use-points-toggle"
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${usePoints ? 'translate-x-5' : ''}`} />
                        </button>
                      </div>
                      {usePoints && (
                        <div className="bg-fire-500/10 border border-fire-500/30 rounded-lg p-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Available</span>
                            <span className="text-white font-bold">{loyaltyPoints} pts (₦{(loyaltyPoints * 10).toLocaleString()})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={Math.min(loyaltyPoints, Math.floor((subtotal + deliveryFee - promoDiscount) / 10))}
                              value={pointsToUse}
                              onChange={e => setPointsToUse(parseInt(e.target.value))}
                              className="flex-1 accent-[#FF4500]"
                              data-testid="points-slider"
                            />
                            <span className="text-fire-500 font-bold text-sm w-20 text-right">-₦{(pointsToUse * 10).toLocaleString()}</span>
                          </div>
                          <p className="text-gray-500 text-xs">Using {pointsToUse} of {loyaltyPoints} points</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 border-t border-white/10 pt-4">
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal</span>
                      <span className="text-white">₦{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Delivery Fee</span>
                      <span className="text-white">₦{deliveryFee.toLocaleString()}</span>
                    </div>
                    {promoDiscount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Promo Discount</span>
                        <span>-₦{promoDiscount.toLocaleString()}</span>
                      </div>
                    )}
                    {pointsDiscount > 0 && (
                      <div className="flex justify-between text-fire-400">
                        <span>Points Discount</span>
                        <span>-₦{pointsDiscount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t border-white/10">
                      <span className="text-white font-bold">Total</span>
                      <span className="text-fire-500 font-bold text-xl">₦{total.toLocaleString()}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-fire-500 hover:bg-fire-600 text-white rounded-full py-4 font-bold text-lg mt-6"
                    data-testid="place-order-btn"
                  >
                    {submitting ? 'Processing...' : paymentMethod === 'card' ? 'Pay Now' : 'Place Order'}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
